import { PRODUCT_RELEASE } from "@/lib/product-release";
import { APP_ENVIRONMENT } from "@/lib/app-environment";

export const PUBLIC_RELEASE_SECTION_NAMES = [
  "Novidades",
  "Melhorias",
  "Correções",
] as const;

export type PublicReleaseSectionName =
  (typeof PUBLIC_RELEASE_SECTION_NAMES)[number];

type LegacyPublicReleaseSectionName =
  | PublicReleaseSectionName
  | "Remoções relevantes";

export type PublicReleaseStatus =
  | "released"
  | "release-candidate"
  | "internal-prelaunch";

export type PublicReleaseNote = {
  version: string;
  codename: string;
  date: string;
  status: PublicReleaseStatus;
  title: string;
  summary: string;
  sections: Partial<Record<LegacyPublicReleaseSectionName, readonly string[]>>;
  changes?: readonly PublicReleaseChangeSource[];
};

export type PublicReleaseChange = {
  type: "Novidade" | "Melhoria" | "Correção";
  module?: string;
  title: string;
  description?: string;
};

type PublicReleaseChangeSource = Omit<PublicReleaseChange, "type"> & {
  type:
    | PublicReleaseChange["type"]
    | "Correção de bug"
    | "Segurança e confiabilidade"
    | "Integração";
};

export const PUBLIC_RELEASE_CHANGE_GROUPS = [
  { type: "Novidade", title: "Novidades" },
  { type: "Melhoria", title: "Melhorias" },
  { type: "Correção", title: "Correções" },
] as const;

const LEGACY_SECTION_TYPE: Record<
  LegacyPublicReleaseSectionName,
  PublicReleaseChange["type"]
> = {
  Novidades: "Novidade",
  Melhorias: "Melhoria",
  Correções: "Correção",
  "Remoções relevantes": "Melhoria",
};

const LEGACY_CHANGE_TYPE: Record<
  PublicReleaseChangeSource["type"],
  PublicReleaseChange["type"]
> = {
  Novidade: "Novidade",
  Melhoria: "Melhoria",
  Correção: "Correção",
  "Correção de bug": "Correção",
  "Segurança e confiabilidade": "Melhoria",
  Integração: "Melhoria",
};

export function getPublicReleaseChanges(
  note: PublicReleaseNote,
): readonly PublicReleaseChange[] {
  if (note.changes?.length) {
    return note.changes.map((change) => ({
      ...change,
      type: LEGACY_CHANGE_TYPE[change.type],
    }));
  }

  return Object.entries(note.sections).flatMap(([section, items]) =>
    (items ?? []).map((title) => ({
      type: LEGACY_SECTION_TYPE[section as LegacyPublicReleaseSectionName],
      title,
    })),
  );
}

export function getPublicReleaseChangeId(
  version: string,
  change: PublicReleaseChange,
) {
  return `${version}:${change.type}:${change.module ?? ""}:${change.title}`
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("pt-BR")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

export function formatPublicReleaseDate(value: string) {
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!dateOnly) return value;

  return new Intl.DateTimeFormat("pt-BR", { timeZone: "UTC" }).format(
    new Date(`${value}T00:00:00Z`),
  );
}

const PUBLIC_RELEASE_NOTE_SOURCE: readonly PublicReleaseNote[] = [
  {
    version: "1.6.7",
    codename: "Argos",
    date: "2026-10-20",
    status: "released",
    title: "Programação mais segura e Gantt mais flexível",
    summary:
      "A versão 1.6.7 facilita a correção de detalhamentos concluídos, o ajuste de atividades no Gantt e o acompanhamento de programações.",
    sections: {},
    changes: [
      {
        type: "Novidade",
        module: "Planejamento",
        title: "Edição autorizada de detalhamentos concluídos",
        description:
          "Usuários autorizados podem corrigir um detalhamento mesmo depois de concluído e de a solicitação avançar no fluxo, preservando a conclusão e o histórico das etapas posteriores.",
      },
      {
        type: "Melhoria",
        module: "Programação > Gantt",
        title: "Arraste e reposicione atividades com mais liberdade",
        description:
          "No Gantt semanal, é possível mover atividades sobre cartões existentes e ajustar rascunhos com mais liberdade, mantendo o alinhamento com os dias da semana.",
      },
      {
        type: "Melhoria",
        module: "Programação",
        title: "Conclusão de atividades de fim de semana",
        description:
          "A classificação considera o prazo de tolerância até a segunda-feira seguinte, evitando marcar como atrasada uma atividade concluída dentro desse período.",
      },
      {
        type: "Correção de bug",
        module: "Programação",
        title: "Desprogramar sem cancelar a ordem externa",
        description:
          "Remover uma programação no Nexus deixa de solicitar o cancelamento da ordem de serviço no WON.",
      },
      {
        type: "Melhoria",
        module: "Programação",
        title: "Sincronização de datas com o WON mais confiável",
        description:
          "A atualização confere as datas da programação antes e depois da alteração, reduzindo o risco de aplicar informações antigas ou repetir uma mudança ainda não confirmada.",
      },
    ],
  },
  {
    version: "1.6.6",
    codename: "Argos",
    date: "2026-10-19",
    status: "released",
    title: "Integração SIGIM mais estável",
    summary:
      "A versão 1.6.6 melhora a identificação de falhas de comunicação SIGIM sem expor credenciais ou mensagens sensíveis.",
    sections: {},
    changes: [
      {
        type: "Melhoria",
        module: "SIGIM",
        title: "Sincronização SIGIM mais estável",
        description:
          "A comunicação com o SIGIM permanece disponível durante a atualização dos dados e apresenta informações mais claras quando há falhas, sem expor credenciais.",
      },
    ],
  },
  {
    version: "1.6.5",
    codename: "Argos",
    date: "2026-10-15",
    status: "released",
    title: "Confiabilidade de Programação, Automações e Materiais",
    summary:
      "A versão 1.6.5 melhora o acompanhamento de programações, materiais e indicadores usados na operação.",
    sections: {},
    changes: [
      {
        type: "Melhoria",
        module: "Automações",
        title: "Acompanhamento de solicitações de cancelamento",
        description:
          "As solicitações têm acompanhamento e histórico próprios para que as pessoas autorizadas possam conferir seu andamento. A execução externa depende da habilitação operacional correspondente.",
      },
      {
        type: "Melhoria",
        module: "Programação",
        title: "Atualização de datas acompanhada",
        description:
          "O Nexus registra o resultado das atualizações enviadas ao WON e evita repetir automaticamente uma alteração quando ainda não foi possível confirmar o resultado.",
      },
      {
        type: "Correção",
        module: "Requisições de Material",
        title: "Conclusão por entrega integral",
        description:
          "A entrega da quantidade total conclui o item sem depender do fechamento administrativo da ordem de compra.",
      },
      {
        type: "Melhoria",
        module: "Planejamento > Gantt",
        title: "Cabeçalho semanal fixo",
        description:
          "Os dias da semana e suas datas permanecem juntos e alinhados enquanto o usuário percorre as atividades do Gantt.",
      },
      {
        type: "Correção",
        module: "Planejamento > Gantt",
        title: "Informações e indicadores sem duplicidade",
        description:
          "O cabeçalho voltou a apresentar a observação da programação e o badge redundante de solicitação crítica foi removido.",
      },
      {
        type: "Novidade",
        module: "ServiceNow > Planejamento",
        title: "Reconciliação controlada de solicitações abertas",
        description:
          "Uma operação administrativa permite reconciliar solicitações abertas do ServiceNow com o fluxo do Planejamento, sem executar um backfill automático durante a implantação.",
      },
      {
        type: "Melhoria",
        module: "Planejamento > Hub",
        title: "Indicadores repetidos no Hub",
        description:
          "O Hub permite configurar mais de uma instância do mesmo indicador, preservando as configurações de cada cartão.",
      },
    ],
  },
  {
    version: "1.6.4",
    codename: "Argos",
    date: "2026-10-10",
    status: "released",
    title: "Regularização de Programação e melhorias de Planejamento",
    summary:
      "A versão 1.6.4 reforça o acompanhamento de programações, as validações do Planejamento e os indicadores por solicitante.",
    sections: {
      Novidades: [
        "Acompanhe a regularização de uma programação quando a solicitação deixa de estar Programável, com prazo de 72 horas e histórico próprio.",
        "Consulte indicadores numéricos do Hub filtrados pelo solicitante da solicitação.",
      ],
      Melhorias: [
        "A lista de solicitações disponíveis na Programação acompanha a semana exibida no Gantt e preserva a programação ativa em outros períodos.",
        "A desaprovação Técnica e PCM apresenta ações e confirmações coerentes com as permissões do usuário.",
      ],
      Correções: [
        "O autor identificado recebe notificação quando um detalhamento é recusado.",
        "Atividades vencidas sem regularização podem ser classificadas como Irregulares, estado distinto de Não concluído.",
      ],
    },
    changes: [
      {
        type: "Novidade",
        module: "Programação",
        title: "Regularização em 72 horas",
        description:
          "Acompanhe o prazo para uma solicitação voltar a Programável. Quando aplicável, o estado Irregular é registrado sem confundi-lo com Não concluído nem reclassificar dados anteriores.",
      },
      {
        type: "Novidade",
        module: "Planejamento > Hub",
        title: "Indicadores por solicitante",
        description:
          "Filtre contagens pelo solicitante informado pelo CMMS e navegue para a lista mantendo o filtro aplicado.",
      },
      {
        type: "Melhoria",
        module: "Programação",
        title: "Lista sincronizada com a semana visível",
        description:
          "A lista considera o período exibido no Gantt, evitando oferecer solicitações já programadas naquele intervalo e preservando opções elegíveis em outras semanas.",
      },
      {
        type: "Melhoria",
        module: "Validação Técnica e PCM",
        title: "Desaprovação com permissões e histórico preservados",
        description:
          "A desaprovação Técnica de solicitações já Programáveis invalida a aprovação PCM quando necessário e retorna o fluxo à etapa técnica, com confirmação e auditoria.",
      },
      {
        type: "Correção de bug",
        module: "Detalhamento",
        title: "Notificação de recusa",
        description:
          "O autor resolvido recebe uma notificação persistente com acesso à solicitação quando o detalhamento é recusado.",
      },
    ],
  },
  {
    version: "1.6.3",
    codename: "Argos",
    date: "2026-10-06",
    status: "released",
    title: "Requisições de Material e Programação",
    summary:
      "A versão 1.6.3 agiliza as Requisições de Material e conecta os indicadores do Planejamento diretamente às listas operacionais da Programação.",
    sections: {
      Novidades: [
        "Registre previsões de entrega diretamente nos materiais e aplique uma previsão a vários itens elegíveis.",
        "Consulte o usuário de cotação de cada material, inclusive antes da emissão da ordem de compra.",
      ],
      Melhorias: [
        "Configure nomes de exibição para usuários de cotação e mantenha a identificação original do material.",
        "Os indicadores do Hub abrem a Lista da Programação já filtrada pelo contexto selecionado.",
      ],
      Correções: [
        "A interação de teclado na previsão inline não aciona mais a abertura da linha da tabela.",
      ],
    },
    changes: [
      {
        type: "Novidade",
        module: "Requisições de Material",
        title: "Previsão direta e em massa",
        description:
          "Atualize a previsão de entrega pela lista, individualmente ou para vários materiais elegíveis.",
      },
      {
        type: "Novidade",
        module: "Requisições de Material",
        title: "Usuário de cotação por material",
        description:
          "Acompanhe quem está cotando cada material, mesmo quando a ordem de compra ainda não foi emitida.",
      },
      {
        type: "Melhoria",
        module: "Planejamento",
        title: "Indicadores ligados à Programação",
        description:
          "Abra a Lista da Programação a partir do indicador correspondente, preservando o filtro durante a navegação.",
      },
    ],
  },
  {
    version: "1.6.2",
    codename: "Argos",
    date: "2026-09-30",
    status: "released",
    title: "Planejamento, Programação e Requisições de Material",
    summary:
      "A versão 1.6.2 fecha os ajustes operacionais de Planejamento e Programação e prepara o acompanhamento de Requisições de Material com sincronização segura.",
    sections: {
      Novidades: [
        "Requisições de Material com operação por item, filtros, prioridade, previsão, seguimento, histórico e notificações.",
        "Registro de necessidade de tamponamento no detalhamento de Planejamento.",
      ],
      Melhorias: [
        "Lista, Calendário, Gantt, cards, modal e séries intersemanais passam a respeitar com mais precisão o estado operacional ativo.",
        "A sincronização provider-agnostic preserva a última base válida, evita publicação parcial e mantém a idempotência.",
        "Transições de página exibem carregamento global consistente e o cadastro de usuário trata e-mail duplicado como conflito funcional.",
      ],
      Correções: [
        "Trechos inativos ou cancelados deixam de reaparecer na operação corrente após reprogramações.",
      ],
    },
    changes: [
      {
        type: "Novidade",
        module: "Requisições de Material",
        title: "Operação RM por item",
        description:
          "Acompanhe materiais, compras, entregas, previsões, histórico, prioridade e seguimento em uma lista operacional integrada ao Nexus.",
      },
      {
        type: "Melhoria",
        module: "Requisições de Material",
        title: "Sincronização segura",
        description:
          "O domínio desacoplado do provider preserva a última base válida, rejeita snapshots parciais e mantém importações idempotentes.",
      },
      {
        type: "Melhoria",
        module: "Planejamento",
        title: "Séries intersemanais",
        description:
          "Lista, Calendário e Gantt ignoram trechos inativos ou cancelados e mantêm a continuidade correta das séries intersemanais.",
      },
      {
        type: "Melhoria",
        module: "Planejamento",
        title: "Detalhamento e validações",
        description:
          "APR, criticidade, validações técnica/PCM e o campo de tamponamento foram consolidados nos fluxos de detalhamento.",
      },
      {
        type: "Correção de bug",
        module: "Usuários",
        title: "E-mail duplicado",
        description:
          "Tentativas de cadastrar e-mail já existente retornam conflito funcional claro, sem erro interno genérico.",
      },
      {
        type: "Melhoria",
        module: "Experiência do Nexus",
        title: "Carregamento entre páginas",
        description:
          "A navegação exibe um indicador global de carregamento durante a transição entre páginas.",
      },
    ],
  },
  {
    version: "1.6.1",
    codename: "Argos",
    date: "2026-09-28",
    status: "released",
    title: "Planejamento e Requisições de Material",
    summary:
      "A versão 1.6.1 amplia o Planejamento, adiciona o acompanhamento de Requisições de Material e fortalece os fluxos de programação e notificações.",
    sections: {
      Novidades: [
        "A nova área de Requisições de Material acompanha requisições, materiais, compras, entregas, previsões e histórico.",
      ],
      Melhorias: [
        "O Hub, a Programação e as interações compartilhadas foram aprimorados com escopo, histórico e operação mais claros.",
      ],
    },
    changes: [
      {
        type: "Novidade",
        module: "Requisições de Material",
        title: "Acompanhamento operacional",
        description:
          "A nova área acompanha requisições, materiais, compras, entregas, previsões e histórico em uma visão operacional integrada ao Nexus.",
      },
      {
        type: "Melhoria",
        module: "Requisições de Material",
        title: "Filtros e acompanhamento",
        description:
          "Filtros por situação, prioridade e vínculo, ações rápidas, ordenação, seguimento e alertas de previsão vencida tornam a operação mais direta.",
      },
      {
        type: "Melhoria",
        module: "Planejamento",
        title: "Builder visual do Hub",
        description:
          "Presets completos do Hub podem ser criados, duplicados e reorganizados com prévia visual, múltiplas instâncias e escopo preservado.",
      },
      {
        type: "Melhoria",
        module: "Planejamento",
        title: "Indicadores e navegação",
        description:
          "Indicadores abrem listas filtradas conforme o escopo configurado, preservando células, grupos e demais refinamentos autorizados.",
      },
      {
        type: "Melhoria",
        module: "Programação",
        title: "Séries intersemanais",
        description:
          "Continuidade, substituição, reprogramação e conversão de séries preservam o histórico e o contexto operacional de cada trecho.",
      },
      {
        type: "Correção de bug",
        module: "Programação",
        title: "Sincronização de datas",
        description:
          "A sincronização com o WON protege intenções vigentes, evita falsos stale e mantém execução controlada e idempotente.",
      },
      {
        type: "Melhoria",
        module: "Experiência do Nexus",
        title: "Interações compartilhadas",
        description:
          "Menus contextuais, submenus, tabelas redimensionáveis, truncamento e filtros compartilhados foram alinhados aos padrões maduros do produto.",
      },
    ],
  },
  {
    version: "1.6.0",
    codename: "Argos",
    date: "2026-09-19",
    status: "released",
    title: "Programação global e evolução do Planejamento",
    summary:
      "A nova release amplia a visibilidade autorizada da Programação, adiciona presets para o Hub e torna mais segura a edição de detalhamentos concluídos.",
    sections: {
      Novidades: [
        "Usuários autorizados a acessar a Programação passam a visualizar o universo global de atividades.",
        "Presets de indicadores permitem organizar a composição do Hub de Planejamento por usuário e perfil.",
      ],
      Melhorias: [
        "Detalhamentos concluídos agora abrem em modo de visualização e exigem uma ação explícita para edição.",
        "Programações intersemanais mantêm seus trechos e contexto de série durante alterações de período.",
      ],
      Correções: [
        "A edição de uma programação intersemanal não é mais interpretada como conclusão antecipada.",
        "A reedição durante a Validação PCM retorna a solicitação para a validação técnica quando necessário.",
      ],
    },
    changes: [
      {
        type: "Novidade",
        module: "Programação",
        title: "Leitura global autorizada",
        description:
          "A permissão de visualização da Programação passa a oferecer leitura global, mantendo as permissões de alteração independentes.",
      },
      {
        type: "Novidade",
        module: "Planejamento",
        title: "Presets do Hub",
        description:
          "A composição dos indicadores do Hub pode ser organizada por presets associados a usuários e perfis.",
      },
      {
        type: "Melhoria",
        module: "Detalhamento",
        title: "Edição explícita",
        description:
          "Detalhamentos concluídos permanecem protegidos em modo de visualização até que o usuário solicite a edição.",
      },
    ],
  },
  {
    version: "1.5.5",
    codename: "Argos",
    date: "2026-09-17",
    status: "released",
    title: "Programação intersemanal em série",
    summary:
      "Programe atividades que atravessam várias semanas com acompanhamento claro de cada trecho da execução.",
    sections: {
      Novidades: [
        "Programações que atravessam múltiplas semanas agora são organizadas em uma série de trechos semanais.",
        "Cada trecho é identificado como Série: Item n/x na Lista, no Gantt e nos dados da programação.",
      ],
      Melhorias: [
        "O período total da série e o período local de cada trecho ficam apresentados separadamente.",
        "Cada trecho mantém seus próprios dados operacionais, como brigada, acompanhamentos e observações.",
      ],
      Correções: [
        "A Lista e o Calendário passam a carregar corretamente o contexto das programações em série.",
        "Programações de uma única semana e programações independentes em semanas distintas continuam compatíveis.",
      ],
    },
    changes: [
      {
        type: "Novidade",
        module: "Programação",
        title: "Programação intersemanal",
        description:
          "Atividades que atravessam várias semanas agora podem ser acompanhadas como uma série de trechos semanais.",
      },
      {
        type: "Melhoria",
        module: "Programação",
        title: "Contexto por trecho",
        description:
          "Cada trecho mantém seus próprios dados operacionais e exibe seu período local junto do período total da série.",
      },
    ],
  },
  {
    version: "1.5.4",
    codename: "Argos",
    date: "2026-09-16",
    status: "released",
    title: "Correções de Planejamento e Programação",
    summary:
      "Esta release corrige conflitos de programação semanal, amplia o controle da validação PCM e padroniza a apresentação da etapa Programável.",
    sections: {
      Correções: [
        "Conflitos ao programar uma atividade em semanas distintas foram corrigidos.",
        "A ação Desaprovar retorna solicitações Programáveis para a Avaliação PCM, preservando os dados da análise.",
      ],
      Melhorias: [
        "Tentativas ativas respeitam a unicidade por atividade e semana.",
        "A nomenclatura visual da etapa foi padronizada como Programável, mantendo PLANEJAVEL como enum técnico.",
      ],
    },
    changes: [
      {
        type: "Correção de bug",
        module: "Planejamento",
        title: "Programação semanal",
        description:
          "A programação simultânea em semanas diferentes passou a respeitar a unicidade correta por atividade e semana.",
      },
      {
        type: "Melhoria",
        module: "Planejamento",
        title: "Validação PCM",
        description:
          "Solicitações já Programáveis podem ser desaprovadas com preservação dos dados e do histórico da análise PCM.",
      },
    ],
  },
  {
    version: "1.5.3",
    codename: "Argos",
    date: "2026-09-16",
    status: "released",
    title: "Base CMMS/WON mais confiável",
    summary:
      "Esta correção impede a publicação de bases parciais e preserva a última base válida quando algum relatório do WON não é baixado ou validado.",
    sections: {
      Correções: [
        "Fontes CMMS temporariamente ausentes após um snapshot incompleto foram recuperadas.",
        "Falhas de download ou merge agora impedem a publicação de uma base parcial.",
      ],
      Melhorias: [
        "A base oficial só é substituída após a validação integral dos relatórios esperados.",
        "A falha do download bloqueia a etapa seguinte da atualização automática.",
      ],
    },
    changes: [
      {
        type: "Correção de bug",
        module: "Integrações",
        title: "Recuperação das fontes CMMS",
        description:
          "A atualização íntegra da base WON restabeleceu as fontes CMMS que haviam ficado ausentes após um snapshot parcial.",
      },
      {
        type: "Melhoria",
        module: "Rotinas",
        title: "Publicação segura da base",
        description:
          "A última base válida é preservada quando os relatórios esperados não são baixados ou validados integralmente.",
      },
    ],
  },
  {
    version: "1.5.2",
    codename: "Argos",
    date: "2026-09-15",
    status: "released",
    title: "Sincronização WON mais segura e rastreável",
    summary:
      "Esta atualização melhora o acompanhamento e a confirmação das alterações de datas enviadas ao WON.",
    sections: {
      Novidades: [
        "O histórico de tentativas da sincronização de datas passou a registrar o resultado efetivo de cada execução.",
      ],
      Melhorias: [
        "A rotina de sincronização agora limita globalmente cada execução a cinco efeitos externos.",
        "Intenções antigas são reconciliadas sem enviar datas obsoletas ao WON.",
      ],
      Correções: [
        "Falhas, leases e tentativas da integração WON passaram a ser tratados de forma controlada, sem limpar silenciosamente efeitos incertos.",
      ],
    },
    changes: [
      {
        type: "Melhoria",
        module: "Programação",
        title: "Sincronização WON controlada",
        description:
          "A sincronização de datas passou a usar lotes limitados, leases, backoff e confirmação explícita do resultado externo.",
      },
      {
        type: "Novidade",
        module: "Automações",
        title: "Histórico de tentativas",
        description:
          "As tentativas da integração mantêm histórico append-only para auditoria e reconciliação.",
      },
      {
        type: "Correção de bug",
        module: "Programação",
        title: "Proteção contra intenções obsoletas",
        description:
          "Intenções antigas não voltam a aplicar datas no WON depois que a programação vigente mudou.",
      },
    ],
  },
  {
    version: "1.5.1",
    codename: "Argos",
    date: "2026-09-15",
    status: "released",
    title: "Correções e evolução da plataforma",
    summary:
      "Esta atualização reúne correções nos fluxos de Planejamento, Programação e APR, além de melhorias na consistência das informações exibidas.",
    sections: {
      Correções: [
        "Melhorias na edição e apresentação de APRs.",
        "Ajustes na consistência das tentativas de Programação, Justificativas, Avaliações e Histórico.",
      ],
      Melhorias: [
        "Novas informações de Programação diretamente na Lista de Planejamento.",
        "A etapa passa a ser apresentada como Programável.",
        "As operações em lote foram simplificadas, mantendo a gestão de Criticidade.",
      ],
      Novidades: [
        "As bases técnicas dos módulos SIGIM e Projects foram incorporadas para continuidade de desenvolvimento e validação.",
      ],
    },
    changes: [
      {
        type: "Correção de bug",
        module: "APR",
        title: "Edição e identificação",
        description:
          "A edição e a apresentação de APRs foram refinadas, incluindo registros sem número de AR.",
      },
      {
        type: "Correção de bug",
        module: "Programação",
        title: "Consistência do histórico",
        description:
          "Tentativas, justificativas, avaliações e autores passaram a ser apresentados de forma consistente.",
      },
      {
        type: "Melhoria",
        module: "Planejamento",
        title: "Resumo de programação",
        description:
          "A Lista de Planejamento ganhou informações mais claras sobre programações e tentativas.",
      },
      {
        type: "Novidade",
        module: "Plataforma",
        title: "Bases técnicas SIGIM e Projects",
        description:
          "As fundações foram incorporadas à base para continuidade de desenvolvimento e validação; ainda não estão disponíveis para uso geral.",
      },
    ],
  },
  {
    version: "1.5.0",
    codename: "Argos",
    date: "2026-09-12",
    status: "released",
    title: "Automações, rastreabilidade e melhorias na Programação",
    summary:
      "A versão 1.5.0 amplia os recursos de automação e rastreabilidade do Nexus, além de trazer melhorias importantes nos fluxos de Planejamento, Programação, APR e Hub.",
    sections: {
      Novidades: [
        "Nova estrutura de automações para tratamento de cancelamentos, com controle de execução, estados e rastreabilidade das ações.",
        "Novo indicador apresenta as solicitações efetivamente abertas no fluxo operacional, desconsiderando itens fora do fluxo e estados já encerrados.",
        "Programações, reprogramações, justificativas, decisões do PCM, conclusões e demais eventos passam a aparecer individualmente na linha do tempo da solicitação.",
      ],
      Melhorias: [
        "Atividades do turno noturno podem ser concluídas no dia seguinte dentro da janela operacional prevista sem serem classificadas incorretamente como atrasadas.",
        "A análise de justificativas de não conclusão ou conclusão em atraso passa a respeitar o PCM responsável pela célula técnica da atividade.",
      ],
      Correções: [
        "A observação registrada durante a aprovação técnica agora é persistida corretamente e permanece disponível após recarregar a solicitação.",
        "Tentativas já encerradas mantêm seu resultado histórico, mesmo quando a solicitação possui novas programações ou execuções posteriores.",
        "Presets de detalhamento passam a restaurar corretamente APR, variantes e seleções associadas, inclusive em cenários com múltiplas APRs.",
      ],
    },
    changes: [
      {
        type: "Novidade",
        module: "Automações",
        title: "Fluxo de cancelamentos",
        description:
          "Nova estrutura para cancelamentos, com controle de execução, estados e rastreabilidade das ações.",
      },
      {
        type: "Correção de bug",
        module: "Planejamento",
        title: "Observação da aprovação técnica",
        description:
          "A observação registrada na aprovação técnica permanece disponível após recarregar a solicitação.",
      },
      {
        type: "Correção de bug",
        module: "Programação",
        title: "Preservação do histórico de tentativas",
        description:
          "Tentativas encerradas mantêm seu resultado histórico mesmo com novas programações ou execuções posteriores.",
      },
      {
        type: "Melhoria",
        module: "Programação",
        title: "Regra para atividades noturnas",
        description:
          "Atividades noturnas podem ser concluídas no dia seguinte dentro da janela operacional prevista sem classificação incorreta de atraso.",
      },
      {
        type: "Melhoria",
        module: "Programação",
        title: "Revisão de justificativas pelo PCM",
        description:
          "A análise de justificativas passa a respeitar o PCM responsável pela célula técnica da atividade.",
      },
      {
        type: "Correção de bug",
        module: "APR",
        title: "Preservação das seleções ao aplicar presets",
        description:
          "Presets restauram APR, variantes e seleções associadas, inclusive em cenários com múltiplas APRs.",
      },
      {
        type: "Novidade",
        module: "Hub",
        title: "Indicador Abertos no fluxo",
        description:
          "Novo indicador apresenta solicitações abertas no fluxo operacional, excluindo itens fora do fluxo e estados encerrados.",
      },
      {
        type: "Novidade",
        module: "Histórico",
        title: "Eventos detalhados da Programação",
        description:
          "A linha do tempo passa a mostrar programações, decisões, justificativas, conclusões e demais eventos individualmente.",
      },
    ],
  },
  {
    version: "1.4.2",
    codename: "Argos",
    date: "2026-09-03",
    status: "released",
    title: "Hotfix operacional do Nexus",
    summary:
      "Hotfix com correções e melhorias nos fluxos de Planejamento, Programação, APR e Hub.",
    sections: {
      Novidades: [
        "Programação para a próxima semana mediante confirmação quando já existe programação vigente.",
        "Cancelamento automático de programação futura quando a solicitação é executada antes do início.",
        "Duplicar indicador no Hub para agilizar a criação de indicadores semelhantes.",
      ],
      Correções: [
        "Retorno ao detalhamento após reprovação do PCM, incluindo solicitações com Detalhamento Crítico.",
        "Navegação dos indicadores do Hub preservando filtros de Célula Técnica e Grupo CMMS.",
        "Cadastro e edição de variantes de APR em requisições multipart, incluindo quantidade zero de bombeiros.",
      ],
    },
    changes: [
      {
        type: "Correção de bug",
        module: "Planejamento",
        title: "Retorno ao detalhamento após reprovação do PCM",
        description:
          "O fluxo retorna corretamente ao detalhamento normal ou crítico e permite nova conclusão.",
      },
      {
        type: "Correção de bug",
        module: "Hub",
        title: "Navegação com filtros preservados",
        description:
          "Indicadores mantêm os filtros de Célula Técnica e Grupo CMMS ao abrir a listagem.",
      },
      {
        type: "Correção de bug",
        module: "APR",
        title: "Variantes em requisições multipart",
        description:
          "Cadastro e edição de variantes preservam corretamente quantidades, inclusive zero bombeiros.",
      },
      {
        type: "Novidade",
        module: "Programação",
        title: "Programação para a próxima semana",
        description:
          "Solicitações já programadas podem ser agendadas na próxima semana mediante confirmação.",
      },
      {
        type: "Novidade",
        module: "Programação",
        title: "Cancelamento automático de programação futura",
        description:
          "A programação futura é cancelada automaticamente quando a solicitação é executada antes do início.",
      },
      {
        type: "Novidade",
        module: "Hub",
        title: "Duplicar indicador",
        description:
          "Indicadores semelhantes podem ser duplicados para agilizar sua criação.",
      },
    ],
  },
  {
    version: "1.4.1",
    codename: "Argos",
    date: "2026-09-02",
    status: "internal-prelaunch",
    title: "Correções técnicas da release",
    summary:
      "Patch técnico de preparação da versão 1.4.1, sem alteração funcional no produto.",
    sections: {
      Correções: [
        "Fixtures de teste e tipagem do DTO de APR ajustadas para manter os gates de qualidade da release.",
      ],
    },
    changes: [
      {
        type: "Correção de bug",
        module: "Plataforma",
        title: "Preparação da release",
        description:
          "Ajustes técnicos de TypeScript e ESLint sem mudança de comportamento funcional.",
      },
    ],
  },
  {
    version: "1.4.0",
    codename: "Argos",
    date: "2026-09-01",
    status: "internal-prelaunch",
    title: "Catálogos e fluxos operacionais mais completos",
    summary:
      "Marco de código fechado em DEV, ainda não promovido para PROD, com avanços em Grupos CMMS, APR, Planejamento e integração BI.",
    sections: {
      Novidades: [
        "Catálogo automático de Grupos CMMS com aliases, anchors, reconciliação de renomes e visibilidade contextual.",
        "Variantes de APR, múltiplas APRs por detalhamento e regra de maior quantitativo de bombeiros na Brigada.",
        "Endpoint dedicado read-only de Programação para Power BI com autenticação BI.",
      ],
      Melhorias: [
        "Líder de Célula habilitado na Validação Técnica e confirmações PCM obrigatórias para aprovação.",
        "Repaginação do Modal de Planejamento e melhorias de UX das APRs.",
        "Contrato BI localizado em português, com datas pt-BR e sanitização de placeholders sol_.",
      ],
      Correções: [
        "Acesso negado à Configuração do Sistema tratado de forma consistente.",
      ],
    },
    changes: [
      {
        type: "Novidade",
        module: "Integrações",
        title: "Programação para Power BI",
        description:
          "Consulta dedicada e somente leitura com uma linha por programação principal.",
      },
      {
        type: "Melhoria",
        module: "Grupos CMMS",
        title: "Catálogo automático",
        description:
          "Aliases e anchors permitem reconciliar renomes preservando a identidade canônica.",
      },
      {
        type: "Melhoria",
        module: "APR",
        title: "Variantes e múltiplas APRs",
        description:
          "O detalhamento suporta variantes e mais de uma APR com quantitativos próprios.",
      },
    ],
  },
  {
    version: "1.3.0",
    codename: "Argos",
    date: "2026-08-28",
    status: "released",
    title: "Fluxos de Planejamento mais consistentes",
    summary:
      "A versão 1.3.0 reforça os fluxos de Planejamento, Programação, APR, Indicadores e integração WON.",
    sections: {
      Melhorias: [
        "Remoções de programação e atividades vinculadas são reconciliadas automaticamente no Gantt e no Controle da Brigada.",
        "Indicadores vinculados podem ser refinados por Célula Técnica ou Grupos CMMS, com prévia numérica antes do salvamento.",
        "A sincronização WON trata geração, concorrência, tentativas, cancelamento e terminalidade CMMS com mais segurança.",
      ],
      Correções: [
        "Corrigida a edição de APR/AR sem falsa duplicidade e a validação de APRs já vinculadas ao Planejamento.",
        "Preferências persistidas, datas de validade e hidratação das tabelas foram estabilizadas.",
        "Atividades vinculadas deixaram de reaparecer como cards independentes após remoção da programação principal.",
      ],
    },
    changes: [
      {
        type: "Correção de bug",
        module: "APR",
        title: "Edição de APR/AR sem falsa duplicidade",
        description:
          "A edição preserva o próprio identificador e as criticidades vinculadas sem acusar duplicidade indevidamente.",
      },
      {
        type: "Correção de bug",
        module: "Programação",
        title: "APR vinculada reconhecida corretamente",
        description:
          "A validação reconhece APRs já vinculadas ao Planejamento durante a programação.",
      },
      {
        type: "Correção de bug",
        module: "Interface",
        title: "Preferências persistidas mais estáveis",
        description:
          "O carregamento de preferências e datas de validade evita inconsistências durante a renderização.",
      },
      {
        type: "Melhoria",
        module: "Programação",
        title: "Remoção de programação vinculada",
        description:
          "A remoção trata atividades derivadas e vínculos de forma consistente.",
      },
      {
        type: "Melhoria",
        module: "Controle da Brigada",
        title: "Cancelamento sincronizado",
        description:
          "Atividades abertas geradas pela programação de origem acompanham seu cancelamento.",
      },
      {
        type: "Melhoria",
        module: "Programação",
        title: "Remoção durante sincronização CMMS",
        description:
          "Programações pendentes de sincronização podem ser removidas com segurança.",
      },
      {
        type: "Melhoria",
        module: "Integração WON",
        title: "Sincronização mais segura",
        description:
          "Geração, concorrência, tentativas e cancelamento são controlados para evitar efeitos obsoletos.",
      },
      {
        type: "Novidade",
        module: "Planejamento",
        title: "Indicadores vinculados refináveis",
        description:
          "Indicadores do Hub podem ser refinados por Célula Técnica ou múltiplos Grupos CMMS.",
      },
      {
        type: "Melhoria",
        module: "Planejamento",
        title: "Prévia dos Indicadores Numéricos",
        description:
          "Os refinamentos atualizam a prévia numérica antes do salvamento.",
      },
      {
        type: "Correção de bug",
        module: "Planejamento",
        title: "Indicadores respeitam refinamentos",
        description:
          "Filtros refinados deixam de apresentar o total geral indevidamente, inclusive com bypass sistêmico.",
      },
      {
        type: "Correção de bug",
        module: "Programação",
        title: "Gantt reconcilia remoções",
        description:
          "Atividades vinculadas não reaparecem como cards independentes após a remoção da programação principal.",
      },
    ],
  },
  {
    version: "1.2.2",
    codename: "Argos",
    date: "2026-08-26",
    status: "released",
    title: "Planejamento e Programação mais consistentes",
    summary:
      "A versão 1.2.2 reuniu melhorias para planejar, programar e acompanhar as atividades com mais clareza.",
    sections: {
      Novidades: [
        "Programação e Controle da Brigada com filtros, exportações, capacidade e Modo Exibição mais consistentes.",
        "Recovery Set versionado integrado às Rotinas do Sistema para recuperação operacional.",
      ],
      Melhorias: [
        "Planejamento, criticidade, validações, APR, Ativos e permissões com fluxos mais robustos.",
        "Sincronização incremental com o WON, tratamento de terminalidade CMMS e validação reforçada de dados.",
      ],
      "Remoções relevantes": [
        "Integração legada do ServiceNow removida da plataforma.",
      ],
    },
    changes: [
      {
        type: "Correção de bug",
        module: "Planejamento",
        title: "Entrada de solicitações críticas no fluxo",
        description:
          "Solicitações vinculadas a Local ou Equipamento crítico entram diretamente no Detalhamento Crítico, independentemente da classificação recebida do CMMS.",
      },
      {
        type: "Melhoria",
        module: "Planejamento",
        title: "Reavaliação automática do backlog",
        description:
          "Solicitações elegíveis acompanham alterações de classificação, Local, Equipamento e configurações de criticidade.",
      },
      {
        type: "Melhoria",
        module: "Planejamento",
        title: "Processamento incremental das sincronizações",
        description:
          "Somente solicitações com informações relevantes alteradas voltam a ser reavaliadas, reduzindo processamento desnecessário.",
      },
      {
        type: "Correção de bug",
        module: "Planejamento",
        title: "Preservação do progresso do workflow",
        description:
          "Sincronizações deixam de reabrir Criticidade ou Detalhamento já concluídos e preservam dados previamente registrados.",
      },
      {
        type: "Correção de bug",
        module: "Planejamento",
        title: "Decisões manuais do fluxo",
        description:
          "Inclusões e remoções manuais são preservadas corretamente durante sincronizações e reavaliações automáticas.",
      },
      {
        type: "Melhoria",
        module: "Planejamento",
        title: "Ações de fluxo em massa",
        description:
          "Foram adicionadas opções para colocar ou retirar múltiplas solicitações do fluxo de Planejamento de uma só vez.",
      },
      {
        type: "Melhoria",
        module: "Planejamento",
        title: "Consulta de etapas fora do fluxo",
        description:
          "Etapas que já possuem informações continuam disponíveis em modo somente leitura quando a solicitação está fora do fluxo.",
      },
      {
        type: "Correção de bug",
        module: "Planejamento",
        title: "Filtros de TAG e Ativo",
        description:
          "O funcionamento dos filtros foi revisado para manter seleção, pesquisa e resultados consistentes.",
      },
      {
        type: "Correção de bug",
        module: "Planejamento",
        title: "Atualização do filtro de TAG",
        description:
          "Foi eliminada a piscada da lista durante alterações no filtro de TAG, mantendo a navegação visualmente estável.",
      },
      {
        type: "Melhoria",
        module: "Programação",
        title: "Padronização dos chips do Gantt",
        description:
          "Cores de criticidade, prioridade, status e indicadores foram alinhadas com a Lista de Planejamento.",
      },
      {
        type: "Correção de bug",
        module: "Programação",
        title: "Equipes de apoio sem solicitação",
        description:
          "A opção Sem solicitação é reconhecida corretamente sem bloquear salvamento ou avanço da Programação.",
      },
      {
        type: "Melhoria",
        module: "Programação",
        title: "Remoção e restauração de equipes de apoio",
        description:
          "Apoios originados do Detalhamento podem ser removidos apenas daquela Programação e posteriormente restaurados.",
      },
      {
        type: "Correção de bug",
        module: "Programação",
        title: "Validação de apoio removido",
        description:
          "Apoios removidos deixam de exigir solicitação vinculada ou a opção Sem solicitação.",
      },
      {
        type: "Correção de bug",
        module: "APR",
        title: "Edição de registros existentes",
        description:
          "Uma APR pode ser editada mantendo seu próprio número sem ser tratada incorretamente como duplicada.",
      },
      {
        type: "Melhoria",
        module: "APR",
        title: "Identificação opcional APR/AR",
        description:
          "O cadastro pode utilizar somente APR ou somente AR, mantendo pelo menos um identificador obrigatório.",
      },
      {
        type: "Melhoria",
        module: "APR",
        title: "Controles de identificação",
        description:
          "As opções Sem nº APR e Sem nº AR foram integradas aos próprios campos, utilizando Não se aplica quando selecionadas.",
      },
      {
        type: "Melhoria",
        module: "APR",
        title: "Apresentação de erros",
        description:
          "Erros funcionais, como identificadores duplicados, passam a ser apresentados por notificações mais claras.",
      },
      {
        type: "Novidade",
        module: "Infraestrutura",
        title: "Recovery Set completo do Nexus",
        description:
          "Foi criado um mecanismo de recuperação contendo banco de dados, uploads e informações necessárias para restauração completa do sistema.",
      },
      {
        type: "Melhoria",
        module: "Infraestrutura",
        title: "Validação e retenção dos backups",
        description:
          "Recovery Sets utilizam manifest, checksums, validação remota e retenção segura dos sete pontos de recuperação mais recentes.",
      },
      {
        type: "Melhoria",
        module: "Infraestrutura",
        title: "Backup dos arquivos dos usuários",
        description:
          "Evidências, APRs, Fórum, Chamados e Comunicados passam a integrar os pontos de recuperação do Nexus.",
      },
      {
        type: "Melhoria",
        module: "Infraestrutura",
        title: "Disaster Recovery validado",
        description:
          "O processo de restauração foi validado em ambiente isolado, confirmando a recuperação funcional do banco e dos arquivos.",
      },
      {
        type: "Melhoria",
        module: "Integrações",
        title: "Remoção do ServiceNow legado",
        description:
          "A implementação antiga do ServiceNow foi removida para permitir uma futura reconstrução do módulo sobre nova arquitetura.",
      },
    ],
  },
  {
    version: "1.2.1",
    codename: "Argos",
    date: "2026-08-26",
    status: "released",
    title: "Notas do Nexus mais fáceis de consultar",
    summary:
      "A versão 1.2.1 reorganizou as notas exibidas na tela inicial e padronizou a apresentação das datas.",
    sections: {},
  },
  {
    version: "1.2.0",
    codename: "Argos",
    date: "2026-08-26",
    status: "released",
    title: "Mais recursos para Planejamento e Operação",
    summary:
      "A versão 1.2.0 reúne melhorias de Programação, Controle da Brigada, Planejamento, Ativos e recuperação operacional.",
    sections: {},
  },
  {
    version: "1.1.0",
    codename: "Argos",
    date: "2026-08-24",
    status: "released",
    title: "Programação operacional mais conectada",
    summary:
      "A versão 1.1.0 amplia a Programação com Modo Exibição, filtros canônicos e melhorias de confiabilidade no Planejamento e na integração WON.",
    sections: {
      Novidades: [
        "Modo Exibição para a Programação do Turno, com sessões independentes, revogáveis e somente leitura.",
        "Filtros de Ativos em Planejamento e Programação e coluna OS na Lista de Planejamento.",
      ],
      Melhorias: [
        "Gantt, vínculos, Hub e drill-down agora compartilham escopos mais consistentes.",
        "O Modo Exibição ganhou filtros locais e suporte a telas operacionais contínuas.",
      ],
      Correções: [
        "Solicitações finalizadas no CMMS deixam de permanecer em retry, e placeholders sol_* não aparecem como OS real.",
        "O WON recebeu fallback de Solicitante e validação reforçada da Classificação de O.S.",
      ],
    },
  },
  {
    version: "1.0.0",
    codename: "Argos",
    date: "2026-08-20",
    status: "released",
    title: "A primeira release oficial do Nexus",
    summary:
      "O Argos estabelece a primeira baseline operacional oficial do Nexus by Innova, reunindo Planejamento, Programação, Brigada, Ativos e Rotinas em uma experiência mais estável e segura.",
    sections: {
      Novidades: [
        "Planejamento com modal mais responsivo, Detalhamento com evidências, contingenciamento, Célula Técnica e Lista configurável.",
        "Controle da Brigada com capacidade, filtros, exportações e relatório semanal PDF com paginação adaptativa.",
        "Sincronização das datas programadas com o WON por rotina operacional com histórico e tratamento de falhas.",
      ],
      Melhorias: [
        "Programação e Planejamento com fluxos, filtros, listas e validações mais consistentes.",
        "Gestão de Ativos com carregamento hierárquico resiliente, inclusive durante expansão lazy da raiz.",
        "Plataforma com melhorias de performance, overlays, segurança e estabilidade transversal.",
      ],
      Correções: [
        "Corrigidos cenários de paginação do PDF da Brigada, carregamento incompleto de Ativos e execução parcial de sincronização WON.",
      ],
    },
  },
  {
    version: "1.0.0-rc.11",
    codename: "Atlas",
    date: "2026-08-13",
    status: "release-candidate",
    title: "Operação mais segura e direta",
    summary:
      "O Atlas evoluiu o Controle da Brigada e a Gestão de Equipes, além de reforçar a governança da Validação Técnica.",
    sections: {
      Novidades: [
        "Gestão de Equipes com tabela configurável, filtros, paginação, edição compacta e exclusão protegida.",
        "Novas categorias e informações operacionais nos lançamentos do Controle da Brigada.",
      ],
      Melhorias: [
        "A Brigada pode assumir e editar a atividade após o aceite, preservando os vínculos com a Programação.",
        "Notificações abrem diretamente a atividade no Controle da Brigada.",
        "Funções e grupos CMMS de Equipes agora são selecionados diretamente no modal.",
      ],
      Correções: [
        "A aprovação técnica do próprio detalhamento foi bloqueada, com exceções controladas para Planejador PCM e bypass explícito.",
        "A suíte ampla de Planejamento foi estabilizada com fixtures alinhadas aos contratos atuais.",
        "A árvore de ativos tolera respostas incompletas sem interromper a interface.",
      ],
    },
  },
  {
    version: "1.0.0-rc.10",
    codename: "Atlas",
    date: "2026-08-12",
    status: "release-candidate",
    title: "Planejamento e acessos mais consistentes",
    summary:
      "O Atlas recebeu melhorias nos fluxos de Planejamento, na capacidade da Brigada e na administração de usuários e permissões.",
    sections: {
      Novidades: [
        "Gestão de usuários modernizada, com jornada de trabalho, reset administrativo de senha e Presets dentro de Usuários.",
        "Editor compartilhado de permissões com Nenhum, Próprio e Global, além de duplicação de Presets.",
      ],
      Melhorias: [
        "Programação e Planejamento com toolbar, listas, vínculos e validação PCM aprimorados.",
        "Histórico de capacidade da Brigada e preferências da tabela resetadas para o novo padrão.",
        "Autorização de importações em lote e permissões próprias/globais do Fórum e de Equipes alinhadas.",
      ],
      Correções: [
        "Corrigidas a persistência e a hidratação da Validação PCM, incluindo evidências de material e catálogo de reprovações.",
      ],
    },
  },
  {
    version: "1.0.0-rc.9",
    codename: "Atlas",
    date: "2026-08-11",
    status: "release-candidate",
    title: "Comunicados mais completos",
    summary:
      "O Atlas recebeu uma experiência mais completa para publicar comunicados, compartilhar imagens e acompanhar avisos importantes no Nexus.",
    sections: {
      Novidades: [
        "Editor de comunicados com formatação, listas, links, imagens inline e anexos.",
        "Comunicados persistentes na Página inicial e avisos públicos de manutenção no login.",
      ],
      Melhorias: [
        "Lista de comunicados com paginação, colunas configuráveis e menu de contexto.",
        "Uploads de rascunhos preservam as imagens durante a edição do comunicado.",
        "Empresas terceirizadas agora ficam organizadas na área Dados e gestão.",
      ],
      Correções: [
        "Corrigido o carregamento de imagens inline antes da publicação.",
      ],
    },
  },
  {
    version: "1.0.0-rc.8",
    codename: "Atlas",
    date: "2026-08-10",
    status: "release-candidate",
    title: "Comunicados e manutenção programada",
    summary:
      "O Atlas recebeu comunicados persistentes e um fluxo de manutenção programada para manter as pessoas informadas durante atualizações do Nexus.",
    sections: {
      Novidades: [
        "Área administrativa de Comunicados com publicação por notificação e popup.",
        "Programação de manutenção com tela dedicada de indisponibilidade.",
        "Confirmação persistente de comunicados importantes por usuário.",
      ],
      Melhorias: [
        "Presença entre abas para evitar que o mesmo popup seja repetido durante uma sessão ativa.",
        "Retorno automático ao Nexus quando a manutenção é encerrada.",
        "Acesso de contingência restrito a usuários com autorização explícita.",
      ],
      Correções: [
        "Corrigido o redirecionamento de sessões bloqueadas durante a abertura inicial da aplicação.",
      ],
    },
  },
  {
    version: "1.0.0-rc.7",
    codename: "Atlas",
    date: "2026-08-10",
    status: "release-candidate",
    title: "Acesso seguro e navegação reorganizada",
    summary:
      "O Atlas recebeu uma estrutura de acesso mais segura pelo domínio oficial e uma navegação lateral reorganizada para tornar os módulos mais fáceis de localizar.",
    sections: {
      Melhorias: [
        "Acesso pelo domínio oficial do Nexus com conexão segura.",
        "Navegação lateral reorganizada por áreas de trabalho.",
        "Programação com acessos agrupados ao Controle da Brigada e à Programação do turno.",
        "Painel de notificações mais compacto, redimensionável e com data e hora das mensagens.",
      ],
      Correções: [
        "Corrigido o login e a permanência da sessão pelo domínio oficial.",
        "Corrigida a atualização da Página inicial sem divergência visual do relógio.",
      ],
    },
  },
  {
    version: "1.0.0-rc.6",
    codename: "Atlas",
    date: "2026-08-08",
    status: "release-candidate",
    title: "Controle da Brigada",
    summary:
      "O Atlas recebeu o Controle da Brigada integrado à Programação, com acompanhamento operacional, capacidade e aprovações. Esta versão é candidata a lançamento e permanece sujeita a validações finais.",
    sections: {
      Novidades: [
        "Controle da Brigada com visões semanal e em Lista.",
        "Capacidade recorrente e exceções para datas específicas.",
        "Aprovação ou recusa de solicitações automáticas da Brigada.",
      ],
      Melhorias: [
        "Integração automática com a Programação, preservando vínculos e informações da atividade.",
        "Notificações persistentes ao planejador sobre decisões da Brigada.",
        "Componentes compartilhados de Gantt, cards, detalhes e menus de contexto.",
        "Fluxo de rascunhos e edição manual com melhor adaptação à tela.",
      ],
    },
  },
  {
    version: "1.0.0-rc.5",
    codename: "Atlas",
    date: "2026-08-05",
    status: "release-candidate",
    title: "Indicadores e análise operacional",
    summary:
      "O Atlas recebeu uma nova camada de análise da programação e uma experiência mais personalizável para acompanhar os indicadores do Nexus. Esta versão é candidata a lançamento e ainda pode receber ajustes de validação.",
    sections: {
      Novidades: [
        "Relatório de Ofensores de Programação com análises, filtros, detalhamento e exportações.",
        "Indicadores numéricos compartilhados entre a Página inicial e o Hub de Planejamento.",
      ],
      Melhorias: [
        "Personalização dos indicadores com escolha de título, cor, tamanho e posição.",
        "Visões LINKED, com dados relacionados aos vínculos do usuário, e GLOBAL, com a visão completa autorizada.",
        "Histórico e dimensões dos relatórios preservados para análises mais confiáveis.",
      ],
    },
  },
  {
    version: "1.0.0-rc.4",
    codename: "Atlas",
    date: "2026-08-03",
    status: "release-candidate",
    title: "Planejamento mais rastreável",
    summary:
      "O Atlas recebeu justificativas mais completas, tratamento automático de atividades atrasadas e regras mais claras para a tolerância de fim de semana. Esta versão antecede o lançamento oficial 1.0.0 e permanece sujeita a ajustes de validação.",
    sections: {
      Novidades: [
        "Justificativas de não conclusão com resultado da execução e fatores estruturados.",
        "Catálogo organizado para apoiar o registro das causas da não execução.",
      ],
      Melhorias: [
        "Atividades atrasadas passam a ser tratadas automaticamente, com melhor rastreabilidade.",
        "A justificativa original é preservada quando a execução é concluída posteriormente.",
        "Tolerância configurável para atividades encerradas no fim de semana.",
        "Acesso às configurações do Planejamento alinhado às permissões efetivas.",
      ],
    },
  },
  {
    version: "1.0.0-rc.3",
    codename: "Atlas",
    date: "2026-08-01",
    status: "release-candidate",
    title: "Programação do turno",
    summary:
      "O Atlas ganhou uma visão operacional de turno para acompanhar as solicitações da semana com paginação adaptável, Modo Exibição e leitura mais direta.",
    sections: {
      Novidades: [
        "Programação do turno com Modo Exibição para acompanhamento operacional.",
        "Solicitações OPEN_DELAYED da semana atual incluídas na visão do turno.",
        "Paginação processada no backend, mantendo solicitações vinculadas junto da principal.",
      ],
      Melhorias: [
        "Seletor de 3 a 10 linhas, com opções individuais.",
        "Quantidade selecionada tratada como teto adaptável ao viewport.",
        "Painel sem scroll vertical e sem linhas parcialmente ocultas.",
      ],
    },
  },
  {
    version: "1.0.0-rc.2",
    codename: "Atlas",
    date: "2026-07-31",
    status: "release-candidate",
    title: "Proteção operacional reforçada",
    summary:
      "O Atlas avançou na preparação para o lançamento com a política inicial de backup do banco e validações de recuperação controlada.",
    sections: {
      Novidades: [
        "Backup diário do banco com retenção de gerações válidas.",
        "Validação de recuperação em ambiente temporário isolado.",
      ],
      Melhorias: [
        "Maior proteção operacional para os dados transacionais do Nexus.",
        "Recuperação preparada para reconstruir as estruturas necessárias às integrações.",
      ],
      Correções: [
        "Ajustada a política de novas tentativas para indisponibilidades temporárias do destino de backup.",
      ],
    },
  },
  {
    version: "1.0.0-rc.1",
    codename: "Atlas",
    date: "2026-07-31",
    status: "release-candidate",
    title: "Candidato ao primeiro lançamento",
    summary:
      "O Atlas reúne a primeira experiência completa do Nexus para validação final. Esta versão antecede o lançamento oficial 1.0.0 e permanece sujeita a ajustes de validação.",
    sections: {
      Novidades: [
        "Página inicial personalizável com widgets e atalhos para a rotina de cada usuário.",
        "Fluxo completo de Planejamento e Programação, incluindo painel operacional, criticidade, detalhamento e validações.",
        "Gestão de APR e presets de detalhamento integrados à preparação das solicitações.",
        "Fórum, Central de Chamados e acompanhamento do ServiceNow reunidos no Nexus.",
      ],
      Melhorias: [
        "Listas e filtros mais consistentes para localizar, organizar e acompanhar informações.",
        "Navegação, notificações e identidade visual revisadas para o uso diário.",
        "Visões operacionais mais claras para equipes, pendências e programação semanal.",
      ],
      Correções: [
        "Ajustados escopos de acesso, filtros e agrupamentos usados no Planejamento e na Programação.",
        "Corrigidas inconsistências visuais e de interação identificadas durante a validação do MVP.",
      ],
    },
  },
  {
    version: "0.4.1",
    codename: "Atlas",
    date: "2026-07-28",
    status: "internal-prelaunch",
    title: "Mais praticidade no seu dia",
    summary:
      "A página inicial recebeu uma central de relógio com alarme e cronômetro, além de ajustes para deixar a navegação mais limpa e compacta.",
    sections: {
      Novidades: [
        "Relógio interativo na barra superior.",
        "Alarme sonoro com opções de som.",
        "Cronômetro integrado ao Nexus.",
      ],
      Melhorias: [
        "Cabeçalho de notificações mais compacto.",
        "Acesso rápido à personalização por ícone.",
        "Visual dos widgets mais limpo.",
      ],
      "Remoções relevantes": [
        "Removidos os horários de última atualização exibidos nos widgets.",
      ],
    },
  },
  {
    version: "0.4.0",
    codename: "Atlas",
    date: "2026-07-28",
    status: "internal-prelaunch",
    title: "Sua página inicial, do seu jeito",
    summary:
      "A página inicial do Nexus agora pode ser organizada de acordo com sua rotina, mantendo suas preferências em qualquer computador ou navegador.",
    sections: {
      Novidades: [
        "Página inicial configurável com widgets.",
        "Organização personalizada de tamanho, posição e cores.",
        "Informações preservadas entre navegadores e dispositivos.",
        "Acompanhamento de Planejamento e Fórum.",
        "Acesso às novidades pela versão exibida no login.",
      ],
      Melhorias: [
        "Prévia em tempo real durante a personalização.",
        "Painel de configurações retrátil.",
        "Atalhos adaptados automaticamente ao tamanho.",
        "Navegação expansível do Planejamento.",
        "Minhas pendências com mais itens disponíveis.",
        "Minha semana e indicadores com opções de escopo.",
      ],
      "Remoções relevantes": [
        "Removida a antiga Visualização por empresa da barra superior.",
        "Removidos os antigos cards técnicos da página inicial.",
      ],
    },
  },
  {
    version: "0.3.0",
    codename: "Atlas",
    date: "2026-07-28",
    status: "internal-prelaunch",
    title: "Mais clareza para planejar e operar",
    summary:
      "O Nexus ganhou melhorias para organizar o planejamento, detalhamento e documentos de apoio da operação.",
    sections: {
      Novidades: [
        "Gestão de APR por empresa, com validade, quantidade necessária de bombeiros e documento protegido.",
        "Presets de Detalhamento com aplicação mais rápida e referências de APR.",
      ],
      Melhorias: [
        "Seleção de APR, criticidades e indicadores com interações mais estáveis.",
        "Listas e menus de ações mais compactos e consistentes.",
      ],
      Correções: [
        "Corrigidas instabilidades de seleção, menus contextuais e escopos de APR.",
      ],
    },
  },
  {
    version: "0.2.0",
    codename: "Atlas",
    date: "2026-07-24",
    status: "internal-prelaunch",
    title: "Mais segurança para a operação",
    summary:
      "A gestão de APR ficou mais estruturada, rastreável e conectada ao planejamento da operação.",
    sections: {
      Novidades: [
        "Cadastro e acompanhamento do ciclo de vida das APRs.",
        "Seleção estruturada de APR no Detalhamento do Planejamento.",
      ],
      Melhorias: [
        "Validade e disponibilidade de APR passaram a ser consideradas durante a programação.",
        "Ações da Gestão de APR foram organizadas em menus mais acessíveis.",
      ],
      Correções: [
        "Ajustadas validações, indicadores e informações de disponibilidade de APR.",
      ],
    },
  },
  {
    version: "0.1.0",
    codename: "Atlas",
    date: "2026-07-23",
    status: "internal-prelaunch",
    title: "Base para atendimento e acompanhamento",
    summary:
      "Marco interno que organizou a Central de Chamados, o acompanhamento e a rastreabilidade operacional do Nexus.",
    sections: {
      Novidades: [
        "Central de Chamados com acompanhamento, triagem e histórico.",
        "Notificações persistentes e protocolos por tipo de chamado.",
      ],
      Melhorias: [
        "Workspace administrativo com filtros e preferências persistentes.",
        "Conversas e anexos protegidos para solicitantes e equipes.",
      ],
    },
  },
] as const;

const EDITORIAL_CHANGE = (
  type: PublicReleaseChange["type"],
  module: string,
  title: string,
  description: string,
): PublicReleaseChange => ({ type, module, title, description });

const PUBLIC_RELEASE_NOTE_EDITORIAL_OVERRIDES: Record<
  string,
  readonly PublicReleaseChange[]
> = {
  "1.6.3": [
    EDITORIAL_CHANGE(
      "Novidade",
      "Requisições de Material",
      "Previsões de entrega mais rápidas",
      "Registre uma previsão diretamente na lista ou aplique a mesma data a vários materiais elegíveis.",
    ),
    EDITORIAL_CHANGE(
      "Novidade",
      "Requisições de Material",
      "Responsável pela cotação",
      "Consulte quem está cotando cada material, inclusive antes da emissão da ordem de compra.",
    ),
    EDITORIAL_CHANGE(
      "Melhoria",
      "Planejamento",
      "Indicadores abrem a lista certa",
      "Ao abrir um indicador, a Lista da Programação mantém o contexto e os filtros selecionados.",
    ),
    EDITORIAL_CHANGE(
      "Correção",
      "Requisições de Material",
      "Previsão sem abrir a linha por engano",
      "A interação por teclado no campo de previsão não aciona mais a abertura do item.",
    ),
  ],
  "1.6.2": [
    EDITORIAL_CHANGE(
      "Novidade",
      "Requisições de Material",
      "Acompanhamento por material",
      "Consulte situação, prioridade, previsão e histórico de cada material em uma lista operacional.",
    ),
    EDITORIAL_CHANGE(
      "Novidade",
      "Planejamento",
      "Registro de necessidade de tamponamento",
      "O detalhamento permite registrar essa necessidade para apoiar a preparação do serviço.",
    ),
    EDITORIAL_CHANGE(
      "Melhoria",
      "Planejamento e Programação",
      "Séries semanais mais consistentes",
      "Lista, Calendário e Gantt acompanham os trechos ativos de cada programação sem reexibir itens cancelados.",
    ),
    EDITORIAL_CHANGE(
      "Melhoria",
      "Requisições de Material",
      "Atualização protege os dados disponíveis",
      "Uma atualização incompleta não substitui as informações válidas já disponíveis.",
    ),
    EDITORIAL_CHANGE(
      "Correção",
      "Usuários",
      "E-mail duplicado informado claramente",
      "O cadastro apresenta uma mensagem funcional quando o endereço já está em uso.",
    ),
    EDITORIAL_CHANGE(
      "Melhoria",
      "Experiência do Nexus",
      "Transições entre páginas mais claras",
      "Um indicador informa quando uma nova página está sendo carregada.",
    ),
  ],
  "1.6.1": [
    EDITORIAL_CHANGE(
      "Novidade",
      "Requisições de Material",
      "Nova área de acompanhamento",
      "Consulte requisições, materiais, compras, entregas, previsões e histórico no Nexus.",
    ),
    EDITORIAL_CHANGE(
      "Melhoria",
      "Requisições de Material",
      "Localização e acompanhamento",
      "Filtros, prioridade, ordenação, seguimento e alertas ajudam a encontrar materiais que exigem atenção.",
    ),
    EDITORIAL_CHANGE(
      "Melhoria",
      "Planejamento",
      "Hub personalizável",
      "Organize indicadores em modelos visuais e aplique a composição adequada ao seu perfil.",
    ),
    EDITORIAL_CHANGE(
      "Melhoria",
      "Planejamento",
      "Indicadores com contexto",
      "Os indicadores abrem listas respeitando os filtros e o escopo autorizado.",
    ),
    EDITORIAL_CHANGE(
      "Melhoria",
      "Programação",
      "Séries entre semanas",
      "Cada trecho da programação mantém seu próprio contexto durante consultas e alterações.",
    ),
    EDITORIAL_CHANGE(
      "Correção",
      "Programação",
      "Atualizações de datas mais consistentes",
      "A programação vigente é preservada quando as informações são sincronizadas.",
    ),
  ],
  "1.6.0": [
    EDITORIAL_CHANGE(
      "Novidade",
      "Programação",
      "Visão global para usuários autorizados",
      "Quem tem acesso pode consultar as atividades do conjunto autorizado, sem ampliar as permissões de alteração.",
    ),
    EDITORIAL_CHANGE(
      "Novidade",
      "Planejamento",
      "Modelos para o Hub",
      "Organize a composição dos indicadores por usuário e perfil.",
    ),
    EDITORIAL_CHANGE(
      "Melhoria",
      "Detalhamento",
      "Edição concluída começa em modo de consulta",
      "O conteúdo permanece protegido até que uma pessoa autorizada escolha Editar.",
    ),
    EDITORIAL_CHANGE(
      "Melhoria",
      "Programação",
      "Séries entre semanas preservam o contexto",
      "Alterações mantêm os trechos e as informações associadas a cada período.",
    ),
    EDITORIAL_CHANGE(
      "Correção",
      "Planejamento",
      "Revisão técnica após nova análise PCM",
      "Quando necessário, a solicitação retorna à etapa técnica sem perder o histórico das decisões.",
    ),
  ],
  "1.5.0": [
    EDITORIAL_CHANGE(
      "Novidade",
      "Automações",
      "Solicitações de cancelamento acompanháveis",
      "Pessoas autorizadas podem registrar e acompanhar pedidos de cancelamento antes da execução externa.",
    ),
    EDITORIAL_CHANGE(
      "Novidade",
      "Planejamento",
      "Indicador de solicitações abertas no fluxo",
      "Consulte quantas solicitações permanecem no processo operacional.",
    ),
    EDITORIAL_CHANGE(
      "Novidade",
      "Histórico",
      "Linha do tempo mais completa",
      "Programações, decisões, justificativas e conclusões ficam disponíveis em eventos separados.",
    ),
    EDITORIAL_CHANGE(
      "Melhoria",
      "Programação",
      "Conclusão de atividades noturnas",
      "Atividades do turno noturno podem ser concluídas no dia seguinte dentro do prazo previsto.",
    ),
    EDITORIAL_CHANGE(
      "Melhoria",
      "Programação",
      "Análise de justificativas pelo PCM",
      "A avaliação considera o PCM responsável pela célula da atividade.",
    ),
    EDITORIAL_CHANGE(
      "Correção",
      "Planejamento",
      "Observação da aprovação técnica preservada",
      "A observação continua disponível depois de salvar e reabrir a solicitação.",
    ),
    EDITORIAL_CHANGE(
      "Correção",
      "Programação",
      "Histórico de resultados preservado",
      "Novas programações não alteram o resultado já registrado para uma tentativa encerrada.",
    ),
    EDITORIAL_CHANGE(
      "Correção",
      "APR",
      "Seleções mantidas ao aplicar modelos",
      "A aplicação de um preset preserva as APRs, variantes e escolhas associadas.",
    ),
  ],
  "1.5.2": [
    EDITORIAL_CHANGE(
      "Melhoria",
      "Programação",
      "Sincronização de datas mais segura",
      "As atualizações conferem a programação vigente e o resultado no WON, reduzindo o risco de reaplicar datas antigas.",
    ),
    EDITORIAL_CHANGE(
      "Melhoria",
      "Automações",
      "Histórico de atualizações disponível",
      "Consulte o resultado das tentativas para apoiar a conferência e o acompanhamento de alterações.",
    ),
  ],
  "1.5.1": [
    EDITORIAL_CHANGE(
      "Correção",
      "APR",
      "Edição e identificação de APRs",
      "Ajustes melhoram a edição e a identificação de registros, inclusive quando não há número de AR.",
    ),
    EDITORIAL_CHANGE(
      "Melhoria",
      "Planejamento",
      "Programação visível na lista",
      "A Lista apresenta informações mais claras sobre programações e seus resultados.",
    ),
    EDITORIAL_CHANGE(
      "Melhoria",
      "Planejamento",
      "Operações em lote mais simples",
      "As ações em lote foram simplificadas sem retirar o controle de criticidade.",
    ),
  ],
  "1.3.0": [
    EDITORIAL_CHANGE(
      "Novidade",
      "Planejamento",
      "Indicadores com filtros ajustáveis",
      "Refine os indicadores por célula técnica ou grupos CMMS e confira os resultados antes de salvar.",
    ),
    EDITORIAL_CHANGE(
      "Melhoria",
      "Programação",
      "Remoção de atividades vinculadas",
      "Ao remover a programação principal, as atividades relacionadas acompanham a alteração sem reaparecer como itens separados.",
    ),
    EDITORIAL_CHANGE(
      "Melhoria",
      "Controle da Brigada",
      "Atividades acompanhando a programação",
      "Atividades abertas vinculadas à programação de origem acompanham seu cancelamento.",
    ),
    EDITORIAL_CHANGE(
      "Melhoria",
      "Programação",
      "Alterações durante sincronizações",
      "Programações pendentes podem ser removidas sem perder a coerência das informações exibidas.",
    ),
    EDITORIAL_CHANGE(
      "Correção",
      "APR",
      "Edição sem alertas indevidos",
      "A edição reconhece o próprio registro e as APRs já vinculadas ao Planejamento.",
    ),
    EDITORIAL_CHANGE(
      "Correção",
      "Planejamento",
      "Indicadores respeitam os filtros",
      "Os resultados refletem os refinamentos escolhidos em vez de mostrar o total geral.",
    ),
  ],
  "1.2.2": [
    EDITORIAL_CHANGE(
      "Melhoria",
      "Novidades do Nexus",
      "Notas de versão mais completas",
      "A tela inicial passou a reunir as principais mudanças do Planejamento, Programação, APR, infraestrutura e integrações em um só lugar.",
    ),
  ],
  "1.2.1": [
    EDITORIAL_CHANGE(
      "Melhoria",
      "Novidades do Nexus",
      "Notas organizadas por assunto",
      "As mudanças passaram a ser apresentadas por categoria, módulo e descrição curta, com datas no formato brasileiro.",
    ),
  ],
  "1.2.0": [
    EDITORIAL_CHANGE(
      "Novidade",
      "Programação",
      "Modo Exibição para acompanhamento",
      "Acompanhe a programação e a Brigada em uma visualização apropriada para telas operacionais.",
    ),
    EDITORIAL_CHANGE(
      "Melhoria",
      "Programação e Controle da Brigada",
      "Filtros e exportações",
      "As listas e exportações apresentam informações mais consistentes para apoiar a rotina.",
    ),
    EDITORIAL_CHANGE(
      "Melhoria",
      "Planejamento e APR",
      "Fluxos de trabalho mais robustos",
      "Validações, criticidade, APR e permissões receberam ajustes para tornar o acompanhamento mais consistente.",
    ),
    EDITORIAL_CHANGE(
      "Melhoria",
      "Integração WON",
      "Atualização de ordens mais confiável",
      "A sincronização valida as informações antes de atualizar ordens e acompanha situações já concluídas.",
    ),
    EDITORIAL_CHANGE(
      "Melhoria",
      "Gestão de Ativos",
      "Consulta de ativos mais resiliente",
      "A navegação por ativos mantém a consulta disponível durante o carregamento dos níveis da árvore.",
    ),
    EDITORIAL_CHANGE(
      "Melhoria",
      "ServiceNow",
      "Integração anterior descontinuada",
      "A integração legada foi removida; a mudança não representa a ativação de uma nova integração.",
    ),
  ],
  "1.4.2": [
    EDITORIAL_CHANGE(
      "Novidade",
      "Programação",
      "Agendamento da próxima semana",
      "Uma solicitação já programada pode receber um novo período na semana seguinte após confirmação.",
    ),
    EDITORIAL_CHANGE(
      "Novidade",
      "Programação",
      "Programações futuras atualizadas após execução",
      "Quando a atividade é executada antes do início previsto, o período futuro deixa de aparecer como ativo.",
    ),
    EDITORIAL_CHANGE(
      "Novidade",
      "Planejamento",
      "Duplicação de indicadores",
      "Crie um indicador semelhante a outro já configurado, reduzindo o trabalho de configuração.",
    ),
    EDITORIAL_CHANGE(
      "Correção",
      "Planejamento",
      "Retorno ao detalhamento",
      "Solicitações recusadas pelo PCM, inclusive as críticas, retornam ao detalhamento correto.",
    ),
    EDITORIAL_CHANGE(
      "Correção",
      "Planejamento",
      "Filtros mantidos ao abrir listas",
      "A navegação do Hub preserva os filtros de célula técnica e grupo CMMS.",
    ),
    EDITORIAL_CHANGE(
      "Correção",
      "APR",
      "Variantes preservadas ao salvar",
      "O cadastro e a edição mantêm corretamente as quantidades informadas, inclusive quando são zero.",
    ),
  ],
  "1.5.5": [
    EDITORIAL_CHANGE(
      "Novidade",
      "Programação",
      "Programação em séries semanais",
      "Atividades que atravessam mais de uma semana podem ser acompanhadas como trechos de uma mesma série.",
    ),
    EDITORIAL_CHANGE(
      "Melhoria",
      "Programação",
      "Informações próprias de cada trecho",
      "Cada semana apresenta seu período e mantém os dados operacionais correspondentes.",
    ),
    EDITORIAL_CHANGE(
      "Correção",
      "Programação",
      "Compatibilidade com programações existentes",
      "Programações de uma semana e atividades independentes em semanas distintas continuam funcionando como antes.",
    ),
  ],
  "1.1.0": [
    EDITORIAL_CHANGE(
      "Novidade",
      "Programação",
      "Modo Exibição para o turno",
      "Compartilhe uma visão somente para consulta, com filtros locais para o acompanhamento operacional.",
    ),
    EDITORIAL_CHANGE(
      "Novidade",
      "Planejamento",
      "Filtro por Ativo e coluna de ordem",
      "Localize solicitações por Ativo e consulte o número da ordem na Lista.",
    ),
    EDITORIAL_CHANGE(
      "Melhoria",
      "Planejamento e Programação",
      "Filtros e vínculos mais consistentes",
      "As telas relacionadas preservam o contexto de navegação e as permissões de acesso.",
    ),
    EDITORIAL_CHANGE(
      "Correção",
      "Integração WON",
      "Informações de ordens mais confiáveis",
      "Solicitações finalizadas deixam de permanecer em processamento e dados incompletos não são apresentados como número de ordem.",
    ),
  ],
  "1.0.0": [
    EDITORIAL_CHANGE(
      "Novidade",
      "Planejamento",
      "Solicitações e documentos em um só fluxo",
      "Acompanhe criticidade, detalhamento, evidências, contingenciamento e célula técnica.",
    ),
    EDITORIAL_CHANGE(
      "Novidade",
      "Controle da Brigada",
      "Acompanhamento semanal e por lista",
      "Consulte capacidade, filtros, exportações e relatórios para organizar a operação.",
    ),
    EDITORIAL_CHANGE(
      "Novidade",
      "Programação",
      "Datas programadas acompanhadas com o WON",
      "Consulte o histórico e o resultado das atualizações das datas das ordens de serviço.",
    ),
    EDITORIAL_CHANGE(
      "Melhoria",
      "Nexus",
      "Navegação e informações mais consistentes",
      "Listas, filtros, validações e telas de ativos ficaram mais previsíveis no uso diário.",
    ),
    EDITORIAL_CHANGE(
      "Correção",
      "Nexus",
      "Ajustes em relatórios, ativos e programação",
      "Foram corrigidos problemas de paginação, carregamento de ativos e atualização das programações.",
    ),
  ],
  "1.0.0-rc.11": [
    EDITORIAL_CHANGE(
      "Novidade",
      "Equipes",
      "Gestão de grupos mais completa",
      "Pesquise, filtre e edite grupos em uma lista mais prática, com proteção contra exclusão indevida.",
    ),
    EDITORIAL_CHANGE(
      "Novidade",
      "Controle da Brigada",
      "Mais informações nos lançamentos",
      "Os registros da Brigada passam a apresentar novas categorias e informações operacionais.",
    ),
    EDITORIAL_CHANGE(
      "Melhoria",
      "Controle da Brigada",
      "Atividades após o aceite",
      "A Brigada pode assumir e editar a atividade depois de aceitá-la, preservando sua ligação com a Programação.",
    ),
    EDITORIAL_CHANGE(
      "Melhoria",
      "Notificações",
      "Acesso direto à atividade",
      "As notificações da Brigada abrem a atividade correspondente.",
    ),
    EDITORIAL_CHANGE(
      "Melhoria",
      "Equipes",
      "Seleção de funções e grupos",
      "As funções e os grupos CMMS podem ser selecionados diretamente no formulário.",
    ),
    EDITORIAL_CHANGE(
      "Correção",
      "Planejamento",
      "Aprovação técnica por outra pessoa",
      "O próprio autor não pode aprovar seu detalhamento, respeitando as exceções autorizadas.",
    ),
    EDITORIAL_CHANGE(
      "Correção",
      "Gestão de Ativos",
      "Consulta resiliente da árvore",
      "Informações incompletas de ativos não interrompem a navegação.",
    ),
    EDITORIAL_CHANGE(
      "Melhoria",
      "Controle da Brigada",
      "Registros operacionais mais claros",
      "Categorias e dados dos lançamentos foram organizados para facilitar a conferência.",
    ),
  ],
  "1.0.0-rc.10": [
    EDITORIAL_CHANGE(
      "Novidade",
      "Usuários",
      "Gestão de acesso reorganizada",
      "A administração reúne jornada de trabalho, redefinição de senha e modelos de permissões.",
    ),
    EDITORIAL_CHANGE(
      "Novidade",
      "Permissões",
      "Escopo de acesso mais claro",
      "Os modelos permitem configurar o alcance das ações e duplicar uma configuração existente.",
    ),
    EDITORIAL_CHANGE(
      "Melhoria",
      "Planejamento e Programação",
      "Fluxos e listas aprimorados",
      "Barra de ações, vínculos e validação PCM receberam ajustes para o uso diário.",
    ),
    EDITORIAL_CHANGE(
      "Melhoria",
      "Controle da Brigada",
      "Histórico de capacidade",
      "Acompanhe alterações de capacidade com preferências de lista atualizadas.",
    ),
    EDITORIAL_CHANGE(
      "Melhoria",
      "Fórum e Equipes",
      "Acessos mais coerentes",
      "As permissões e importações respeitam o escopo configurado para cada pessoa.",
    ),
    EDITORIAL_CHANGE(
      "Correção",
      "Validação PCM",
      "Informações preservadas",
      "A validação mantém as evidências de material e as opções de reprovação selecionadas.",
    ),
  ],
  "1.0.0-rc.3": [
    EDITORIAL_CHANGE(
      "Novidade",
      "Programação do turno",
      "Visão operacional da semana",
      "Consulte as solicitações da semana em uma tela preparada para acompanhamento do turno.",
    ),
    EDITORIAL_CHANGE(
      "Novidade",
      "Programação do turno",
      "Solicitações atrasadas incluídas",
      "Atividades ainda abertas da semana atual permanecem visíveis na consulta do turno.",
    ),
    EDITORIAL_CHANGE(
      "Novidade",
      "Programação do turno",
      "Atividades vinculadas permanecem juntas",
      "A navegação entre páginas não separa as solicitações relacionadas à atividade principal.",
    ),
    EDITORIAL_CHANGE(
      "Melhoria",
      "Programação do turno",
      "Lista adaptada à tela",
      "A quantidade de linhas se ajusta ao espaço disponível, sem exigir rolagem vertical interna.",
    ),
  ],
  "1.0.0-rc.2": [
    EDITORIAL_CHANGE(
      "Novidade",
      "Proteção de dados",
      "Pontos de recuperação periódicos",
      "O Nexus mantém cópias do banco para apoiar a recuperação dos dados.",
    ),
    EDITORIAL_CHANGE(
      "Melhoria",
      "Recuperação",
      "Restauração verificada em ambiente isolado",
      "O processo de recuperação é conferido sem interferir nos dados em uso.",
    ),
    EDITORIAL_CHANGE(
      "Correção",
      "Proteção de dados",
      "Falhas temporárias tratadas",
      "Indisponibilidades do destino de cópia não interrompem silenciosamente as tentativas de proteção.",
    ),
  ],
  "1.4.0": [
    EDITORIAL_CHANGE(
      "Melhoria",
      "Nexus",
      "Marco interno de evolução do produto",
      "Esta versão reúne trabalho de preparação e validação, sem representar a disponibilização oficial dos recursos em produção.",
    ),
  ],
  "1.4.1": [
    EDITORIAL_CHANGE(
      "Melhoria",
      "Nexus",
      "Ajustes internos de qualidade",
      "Este marco técnico não alterou a experiência ou as funcionalidades disponíveis aos usuários.",
    ),
  ],
};

export const PUBLIC_RELEASE_NOTES: readonly PublicReleaseNote[] =
  PUBLIC_RELEASE_NOTE_SOURCE.map((note) => ({
    ...note,
    sections: {},
    changes:
      PUBLIC_RELEASE_NOTE_EDITORIAL_OVERRIDES[note.version] ??
      getPublicReleaseChanges(note),
  }));

function compareVersions(left: string, right: string) {
  const [leftCore, leftPrerelease] = left.split("-");
  const [rightCore, rightPrerelease] = right.split("-");
  const leftParts = leftCore.split(".").map(Number);
  const rightParts = rightCore.split(".").map(Number);

  for (let index = 0; index < 3; index += 1) {
    if (leftParts[index] !== rightParts[index]) {
      return leftParts[index] - rightParts[index];
    }
  }

  if (leftPrerelease && !rightPrerelease) return -1;
  if (!leftPrerelease && rightPrerelease) return 1;
  if (leftPrerelease && rightPrerelease) {
    return (
      Number(leftPrerelease.split(".").at(-1)) -
      Number(rightPrerelease.split(".").at(-1))
    );
  }

  return 0;
}

export function validatePublicReleaseNotes(
  notes: readonly PublicReleaseNote[] = PUBLIC_RELEASE_NOTES,
  options: { requireOfficialCurrent?: boolean } = {},
) {
  const requireOfficialCurrent =
    options.requireOfficialCurrent ?? APP_ENVIRONMENT === "production";
  const versions = new Set<string>();
  const internalNotes = notes.filter(
    (note) => note.status === "internal-prelaunch",
  );

  notes.forEach((note) => {
    if (!/^\d+\.\d+\.\d+(?:-(?:beta|rc)\.\d+)?$/.test(note.version)) {
      throw new Error(`Nota pública tem versão inválida: ${note.version}`);
    }

    if (
      !["released", "release-candidate", "internal-prelaunch"].includes(
        note.status,
      )
    ) {
      throw new Error(`Nota pública tem status inválido: ${note.version}`);
    }

    if (versions.has(note.version)) {
      throw new Error(`Nota pública duplicada: ${note.version}`);
    }
    versions.add(note.version);

    const parsedDate = new Date(`${note.date}T00:00:00Z`);
    if (
      !/^\d{4}-\d{2}-\d{2}$/.test(note.date) ||
      Number.isNaN(parsedDate.getTime()) ||
      parsedDate.toISOString().slice(0, 10) !== note.date
    ) {
      throw new Error(`Nota pública tem data inválida: ${note.version}`);
    }
  });

  notes.forEach((note, index) => {
    if (
      index > 0 &&
      compareVersions(notes[index - 1].version, note.version) < 0
    ) {
      throw new Error(
        "As notas públicas devem estar da versão mais recente para a mais antiga",
      );
    }
    if (index > 0 && notes[index - 1].date < note.date) {
      throw new Error(
        "As notas públicas devem estar em ordem cronológica decrescente",
      );
    }

    const changes = getPublicReleaseChanges(note);
    if (!note.codename.trim() || !note.title.trim() || !note.summary.trim()) {
      throw new Error(`Nota pública incompleta: ${note.version}`);
    }
    if (changes.length === 0) {
      throw new Error(`Nota pública sem mudanças: ${note.version}`);
    }

    const ids = new Set<string>();
    changes.forEach((change) => {
      if (
        typeof change.title !== "string" ||
        !change.title.trim() ||
        !change.type
      ) {
        throw new Error(
          `Mudança pública incompleta: ${note.version} (${JSON.stringify(change)})`,
        );
      }
      const id = getPublicReleaseChangeId(note.version, change);
      if (ids.has(id)) {
        throw new Error(`Mudança pública duplicada: ${id}`);
      }
      ids.add(id);
    });

    if (note.status === "released") {
      const content = [
        note.title,
        note.summary,
        ...changes.flatMap((change) => [
          change.title,
          change.description ?? "",
        ]),
      ].join(" ");
      if (
        /candidato(?:\s+em\s+desenvolvimento|\s+a\s+lançamento)?|em validação no ambiente dev|aguardando homologação|notas finais serão publicadas/i.test(
          content,
        )
      ) {
        throw new Error(`Nota oficial ${note.version} contém texto provisório`);
      }
    }
  });

  internalNotes.forEach((note, index) => {
    if (
      index > 0 &&
      compareVersions(internalNotes[index - 1].version, note.version) < 0
    ) {
      throw new Error(
        "Marcos internos devem estar da versão mais recente para a mais antiga",
      );
    }
  });

  const currentNote = notes.find(
    (note) => note.version === PRODUCT_RELEASE.version,
  );
  if (!currentNote) {
    throw new Error(
      `A versão atual ${PRODUCT_RELEASE.version} não possui nota pública`,
    );
  }

  if (currentNote.codename !== PRODUCT_RELEASE.name) {
    throw new Error(
      `O codinome da versão ${PRODUCT_RELEASE.version} não corresponde ao package.json`,
    );
  }

  if (notes[0] !== currentNote) {
    throw new Error("A versão atual deve ser a primeira nota pública");
  }

  if (requireOfficialCurrent && currentNote.status !== "released") {
    throw new Error(
      `A versão atual ${currentNote.version} precisa estar marcada como lançamento oficial no build de produção`,
    );
  }
}

validatePublicReleaseNotes();
